import { z } from 'zod';

export const SanityDocumentSchema = z.object({
  _id: z.string(),
  _type: z.string(),
  _rev: z.string().optional(),
  _createdAt: z.string().optional(),
  _updatedAt: z.string().optional()
});

export const SanityDocumentLikeSchema = SanityDocumentSchema.passthrough();

export const SanitySlugSchema = z.object({
  current: z.string()
});

export const SanityReferenceSchema = z.object({
  _type: z.literal('reference'),
  _ref: z.string()
});

export const SanityAssetSchema = z.object({
  _id: z.string(),
  _type: z.literal('sanity.imageAsset'),
  url: z.string().optional(),
  path: z.string().optional(),
  assetId: z.string().optional(),
  extension: z.string().optional(),
  originalFilename: z.string().optional()
});

export const SanityImageAssetSchema = SanityAssetSchema.extend({
  _type: z.literal('sanity.imageAsset'),
  altText: z.string().nullish(),
  description: z.string().nullish(),
  metadata: z
    .object({
      dimensions: z.object({
        aspectRatio: z.number(),
        height: z.number(),
        _type: z.string(),
        width: z.number()
      })
    })
    .optional()
});

export const SanityImageObjectSchema = z.object({
  asset: SanityReferenceSchema.or(SanityImageAssetSchema),
  crop: z
    .object({
      _type: z.string(),
      left: z.number(),
      bottom: z.number(),
      right: z.number(),
      top: z.number()
    })
    .optional(),
  hotspot: z
    .object({
      _type: z.string(),

      width: z.number(),
      height: z.number(),
      x: z.number(),
      y: z.number()
    })
    .optional()
});

export const SanityImageWithAssetStubSchema = z.object({
  asset: z.object({
    url: z.string()
  })
});

export const SanityAssetSourceSchema = z
  .string()
  .or(SanityReferenceSchema)
  .or(SanityImageAssetSchema);

export const SanityFileAssetSchema = SanityAssetSchema.extend({
  _type: z.literal('sanity.fileAsset'),
  mimeType: z.string()
});

export const SanityFileObjectSchema = z.object({
  asset: SanityFileAssetSchema.or(SanityReferenceSchema)
});

export const SanityDocumentAssetSourceSchema = z
  .string()
  .or(SanityReferenceSchema)
  .or(SanityFileAssetSchema);

export const SanityModalSchema = SanityDocumentSchema.extend({
  variant: z.union([z.literal('topLeft'), z.literal('bottomCenter')]),
  altText: z.string().nullish(),
  layout: z.object({
    gridItems: z
      .array(
        z.object({
          componentType: z
            .union([
              z.literal('dynamic'),
              z.literal('divider'),
              z.literal('portableText'),
              z.literal('modalText')
            ])
            .optional()
            .nullable(),
          component: z
            .union([SanityReferenceSchema, SanityDocumentLikeSchema])
            .nullable()
            .optional(),
          placements: z.array(
            z.object({
              grid: z.string(),
              columnSpan: z.number(),
              columnStart: z.number(),
              rowSpan: z.number(),
              rowStart: z.number()
            })
          )
        })
      )
      .nullable()
      .optional()
  })
});

export const SanityBlockSchema = z.object({
  _key: z.string(),
  _type: z.literal('block').or(z.string()),
  children: z
    .array(
      z.object({
        _key: z.string(),
        _type: z.string(),
        marks: z.array(z.string()).nullish(),
        text: z.string().optional()
      })
    )
    .optional(),
  markDefs: z
    .array(
      z.object({
        _key: z.string(),
        _type: z.string(),
        href: z.string().nullish(),
        type: z.string().nullish(),
        url: z.string().nullish(),
        anchorId: z.string().nullish(),
        anchorKey: z.string().nullish(),
        declineCookie: z.boolean().nullish(),
        modal: SanityModalSchema.nullish()
      })
    )
    .optional()
    .nullable(),
  style: z.string().optional(),
  level: z.number().optional(),
  listItem: z.string().optional()
});

export type SanityImageAsset = z.infer<typeof SanityImageAssetSchema>;
export type SanityFileAsset = z.infer<typeof SanityFileAssetSchema>;
export type SanityBlock = z.infer<typeof SanityBlockSchema>;
export type SanityDocument = z.infer<typeof SanityDocumentSchema>;
export type SanityLikeDocument = z.infer<typeof SanityDocumentLikeSchema>;
export type SanityReference = z.infer<typeof SanityReferenceSchema>;
export type SanityAssetSource = z.infer<typeof SanityAssetSourceSchema>;
export type SanityDocumentAssetSource = z.infer<typeof SanityDocumentAssetSourceSchema>;
